import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, TextInput, Animated, Easing, Alert } from 'react-native';
import LoadingSpinner from '../components/LoadingSpinner';
import { ScreenBackground } from '../components/ThemeGradients';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import Svg, { Path, LinearGradient as SvgLinearGradient, Defs, Stop, G, ClipPath, Rect } from 'react-native-svg';
import { useResetPassword } from '../hooks/usePasswordReset';
import GradientText from '../components/GradientText';
import { useUiStyle } from '../context/UiStyleContext';
import { lookSheets, useLookStyles, useOriginalPolish } from './originalLook';

export const Input = ({ placeholder, value, onChangeText, keyboardType = 'default', secureTextEntry = false, disabled = false, placeholderTextColor = "rgba(255, 255, 255, 0.40)", label, autoComplete, onSubmitEditing }) => {
    const styles = useLookStyles(SHEETS);
    const { isFloodlight } = useUiStyle();
    const [isFocused, setIsFocused] = useState(false);
    const focusT = useRef(new Animated.Value(0)).current;
    const onFocus = () => {
        setIsFocused(true);
        Animated.timing(focusT, { toValue: 1, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };
    const onBlur = () => {
        setIsFocused(false);
        Animated.timing(focusT, { toValue: 0, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };
    return (
        <View style={[styles.inputContainer, isFocused && (isFloodlight ? styles.inputContainerFocused : { borderColor: 'rgba(255, 255, 255, 0.9)' })]}>
            <TextInput
                placeholder={placeholder}
                placeholderTextColor={placeholderTextColor}
                style={styles.input}
                value={value}
                onChangeText={onChangeText}
                keyboardType={keyboardType}
                secureTextEntry={secureTextEntry}
                onFocus={onFocus}
                onBlur={onBlur}
                editable={!disabled}
                autoCapitalize="none"
                {...(isFloodlight ? { accessibilityLabel: label || placeholder, autoComplete, onSubmitEditing } : {})}
            />
        </View>
    );
};

export default function ResetPasswordScreen() {
    const {
        password, setPassword, confirmPassword, setConfirmPassword,
        token, loading, error, success,
        showPassword, setShowPassword, showConfirmPassword, setShowConfirmPassword,
        handleResetPassword, goToLogin, goToForgot,
    } = useResetPassword();
    const styles = useLookStyles(SHEETS);
    const { isFloodlight, root } = useOriginalPolish();
    const fl = (props) => (isFloodlight ? props : {});

    if (success) {
        return (
            <View style={styles.container} {...root}>
                <ScreenBackground />
                <View style={styles.gradient}>
                    <View style={styles.centerContainer}>
                        <View style={styles.successContainer} {...fl({ accessibilityRole: 'status' })}>
                            <Ionicons name="checkmark-circle" size={64} color={isFloodlight ? '#A78BFA' : '#fbbf24'} />
                            <Text style={styles.successTitle} {...fl({ accessibilityRole: 'header' })}>Password Reset Successfully!</Text>
                            <Text style={styles.successText}>
                                Redirecting you to login...
                            </Text>
                        </View>
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container} {...root}>
            <ScreenBackground />
            <View style={styles.gradient}>
                {/* Back button */}
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={goToLogin}
                    {...fl({ accessibilityRole: 'link', accessibilityLabel: 'Back to login' })}
                >
                    <Ionicons name="arrow-back" size={24} color="#fff" />
                </TouchableOpacity>

                <View style={styles.centerContainer}>
                    <View style={styles.formContainer}>
                        {/* Title */}
                        <View style={styles.titleContainer}>
                            <GradientText style={styles.title}>
                                Reset Password
                            </GradientText>
                            <Text style={styles.subtitle}>
                                Enter your new password below
                            </Text>
                        </View>

                        {/* Error message */}
                        {error ? (
                            <View style={styles.errorContainer} {...fl({ accessibilityRole: 'alert' })}>
                                <Ionicons name="alert-circle" size={20} color="#ef4444" />
                                <Text style={styles.errorText}>{error}</Text>
                                {isFloodlight && !token ? (
                                    <TouchableOpacity onPress={goToForgot} accessibilityRole="link" accessibilityLabel="Request a new link" hitSlop={8}>
                                        <Text style={styles.newLinkText}>Request a new link</Text>
                                    </TouchableOpacity>
                                ) : null}
                            </View>
                        ) : null}

                        {/* Password input */}
                        <View style={styles.inputWrapper}>
                            <Text style={styles.inputLabel}>New Password</Text>
                            <View style={styles.passwordInputContainer}>
                                <Input
                                    placeholder="Enter new password"
                                    value={password}
                                    onChangeText={setPassword}
                                    secureTextEntry={!showPassword}
                                    disabled={loading || !token}
                                    label="New password"
                                    autoComplete="new-password"
                                />
                                <TouchableOpacity
                                    style={styles.eyeIcon}
                                    onPress={() => setShowPassword(!showPassword)}
                                    {...fl({ accessibilityRole: 'button', accessibilityLabel: showPassword ? 'Hide password' : 'Show password' })}
                                >
                                    <Ionicons
                                        name={showPassword ? "eye-off" : "eye"}
                                        size={20}
                                        color="rgba(255, 255, 255, 0.5)"
                                    />
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* Confirm Password input */}
                        <View style={styles.inputWrapper}>
                            <Text style={styles.inputLabel}>Confirm Password</Text>
                            <View style={styles.passwordInputContainer}>
                                <Input
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChangeText={setConfirmPassword}
                                    secureTextEntry={!showConfirmPassword}
                                    disabled={loading || !token}
                                    label="Confirm password"
                                    autoComplete="new-password"
                                    onSubmitEditing={() => { if (!loading && token) handleResetPassword(); }}
                                />
                                <TouchableOpacity
                                    style={styles.eyeIcon}
                                    onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                                    {...fl({ accessibilityRole: 'button', accessibilityLabel: showConfirmPassword ? 'Hide confirm password' : 'Show confirm password' })}
                                >
                                    <Ionicons
                                        name={showConfirmPassword ? "eye-off" : "eye"}
                                        size={20}
                                        color="rgba(255, 255, 255, 0.5)"
                                    />
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* Submit button */}
                        <TouchableOpacity
                            style={[
                                styles.submitButton,
                                (loading || !token) && styles.submitButtonDisabled
                            ]}
                            onPress={handleResetPassword}
                            disabled={loading || !token}
                            {...fl({ accessibilityRole: 'button', accessibilityLabel: 'Reset password' })}
                        >
                            {loading ? (
                                <LoadingSpinner size="small" />
                            ) : (
                                <Text style={styles.submitButtonText}>Reset Password</Text>
                            )}
                        </TouchableOpacity>

                        {/* Back to login link */}
                        <TouchableOpacity
                            style={styles.loginLink}
                            onPress={goToLogin}
                            {...fl({ accessibilityRole: 'link', accessibilityLabel: 'Back to login' })}
                        >
                            <Text style={styles.loginLinkText}>
                                Back to Login
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </View>
    );
}

// Floodlight-only refinements on top of the Archivo retype (Classic never sees these).
// The amber accent becomes the site's purple / blue so this page matches the other auth screens.
const FL_LINK = 'rgba(150, 185, 255, 0.95)';
const FL_OVERRIDES = {
    backButton: { padding: 10, borderRadius: 999, minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)' },
    title: { fontWeight: '700', letterSpacing: -0.8, lineHeight: 40 },
    subtitle: { fontSize: 15, lineHeight: 22 },
    inputLabel: { fontSize: 11, fontWeight: '600', letterSpacing: 1.1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.58)' },
    inputContainer: {
        backgroundColor: 'rgba(255,255,255,0.03)',
        minHeight: 48,
        justifyContent: 'center',
        ...(Platform.OS === 'web' ? { transition: 'border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease' } : {}),
    },
    inputContainerFocused: {
        borderColor: 'rgba(167,139,250,0.75)',
        backgroundColor: 'rgba(124,58,237,0.06)',
        ...(Platform.OS === 'web' ? { boxShadow: '0 0 0 3px rgba(124,58,237,0.22)' } : {}),
    },
    eyeIcon: { top: 4, right: 4, width: 40, height: 40, alignItems: 'center', justifyContent: 'center', borderRadius: 8 },
    submitButton: {
        backgroundColor: '#7C3AED',
        ...(Platform.OS === 'web' ? { backgroundImage: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 55%, #2563EB 100%)', transition: 'transform 200ms ease, box-shadow 200ms ease, opacity 200ms ease' } : {}),
    },
    submitButtonText: { color: '#FFFFFF', letterSpacing: 0.3 },
    loginLink: { alignSelf: 'center', minHeight: 40, justifyContent: 'center', paddingHorizontal: 10 },
    loginLinkText: { color: FL_LINK },
    errorText: { color: '#F87171' },
    successContainer: { borderRadius: 20, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)' },
    successTitle: { letterSpacing: -0.4 },
    newLinkText: { color: FL_LINK, fontSize: 14, fontWeight: '600', marginLeft: 8 },
};

const SHEETS = lookSheets({
    container: {
        flex: 1,
        backgroundColor: '#000000',
    },
    gradient: {
        flex: 1,
    },
    backButton: {
        position: 'absolute',
        top: Platform.OS === 'web' ? 32 : 60,
        left: 24,
        zIndex: 10,
        padding: 8,
    },
    centerContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    formContainer: {
        width: '100%',
        maxWidth: 440,
    },
    titleContainer: {
        marginBottom: 32,
    },
    title: {
        fontSize: 32,
        fontWeight: '800',
        color: '#fff',
        marginBottom: 8,
        textAlign: 'center',
    },
    subtitle: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
    },
    errorContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 1,
        borderColor: 'rgba(239, 68, 68, 0.3)',
        borderRadius: 12,
        padding: 12,
        marginBottom: 24,
    },
    errorText: {
        color: '#ef4444',
        fontSize: 14,
        marginLeft: 8,
        flex: 1,
    },
    inputWrapper: {
        marginBottom: 20,
    },
    inputLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.8)',
        marginBottom: 8,
    },
    passwordInputContainer: {
        position: 'relative',
    },
    eyeIcon: {
        position: 'absolute',
        right: 16,
        top: 16,
        zIndex: 10,
    },
    inputContainer: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    input: {
        color: '#fff',
        fontSize: 16,
        paddingRight: 40, // Space for eye icon
    },
    submitButton: {
        backgroundColor: '#fbbf24',
        borderRadius: 12,
        paddingVertical: 16,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 8,
        minHeight: 52,
    },
    submitButtonDisabled: {
        opacity: 0.5,
    },
    submitButtonText: {
        color: '#0f0f17',
        fontSize: 16,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    loginLink: {
        marginTop: 24,
        alignItems: 'center',
    },
    loginLinkText: {
        color: '#fbbf24',
        fontSize: 14,
        fontWeight: '600',
    },
    successContainer: {
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 16,
        padding: 40,
    },
    successTitle: {
        fontSize: 24,
        fontWeight: '700',
        color: '#fff',
        marginTop: 16,
        marginBottom: 8,
        textAlign: 'center',
    },
    successText: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
    },
    inputContainerFocused: {},
    newLinkText: {},
}, FL_OVERRIDES);
